
















































































import ConfirmActionDialog from "@/components/utility/ConfirmActionDialog.vue";
import { IControlElements } from "@/components/utility/TableWrapper.vue";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { detailedDate } from "@/lib/utility/date-helper";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { IPageFilterElement, PageFilterElement } from "@/models/page-filter-element.entity";
import { IReference, Reference } from "@/models/reference.entity";
import { IReport } from "@/models/report.entity";
import { ISharedContent } from "@/models/shared-content.entity";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { ReportFleetSignDocumentModule } from "@/store/modules/report-fleet-sign-document-store";
import { SharedContentModule } from "@/store/modules/shared-content.store";
import DocumentTable from "@/views/sign/DocumentTable.vue";
import { Component, Prop } from "vue-property-decorator";
import RefsSharedContent from "@/components/utility/RefsSharedContent.vue";
import { ISignDocument } from "@/models/sign-document.entity";
import { handleError } from "@/lib/utility/handleError";
import SharedContentSideCard from "@/components/partner/SharedContentSideCard.vue";

enum StepWindow {
  PICK = 0,
  CONFIRM = 1
}

@Component({
  components: { DocumentTable, ConfirmActionDialog, RefsSharedContent, SharedContentSideCard },
  filters: { detailedDate }
})
class PartnerReportVehicleFleetDocumentTable extends PermissionMixin {
  readonly BackendResourceEnum = BackendResourceEnum;
  readonly StepWindow = StepWindow;

  @Prop({ required: true })
  readonly report!: IReport;

  isDialogActive = false;

  isDialogLoading = false;

  sharedContents: ISharedContent[] = [];

  selectedDocument: ISignDocument | null = null;

  openedSharedDocument: ISharedContent | null = null;

  step: StepWindow = StepWindow.PICK;

  selectedSharing: ISharedContent | null = null;

  get additionalControlElements(): IControlElements[] {
    return [
      {
        action: this.openShareDialog,
        icon: "mdi-share",
        loading: this.isDialogLoading
      }
    ];
  }

  get reportFleetSignDocumentModule() {
    return ReportFleetSignDocumentModule;
  }

  get documentFleetRefs(): IReference[] {
    const refs: IReference[] = [];

    refs.push(
      new Reference({
        refId: this.report.fleetId,
        refType: BackendResourceEnum.FLEET
      })
    );

    return refs;
  }

  get documentFleetFilter() {
    const filters: IPageFilterElement[] = [];

    filters.push(
      new PageFilterElement({
        key: "refs.refId",
        operation: PageFilterOperationEnum.EQUAL,
        value: this.report.fleetId
      })
    );

    return filters;
  }

  setOpenedSharedDocument(sharedContent?: ISharedContent) {
    this.openedSharedDocument = null;
    this.$nextTick(() => {
      this.openedSharedDocument = sharedContent || null;
    });
  }

  async fetchSharedDocuments() {
    const reportId = this.report.id;

    SharedContentModule.setFilters([
      new PageFilterElement({ key: "refs.refId", operation: PageFilterOperationEnum.EQUAL, value: reportId.toString() })
    ]);

    const found = (await SharedContentModule.fetchFirstPage({ partnerId: this.report.partnerId })) ?? [];

    this.sharedContents.splice(0);
    this.sharedContents.push(...found);
  }

  async openShareDialog(document: ISignDocument) {
    this.selectedDocument = document;

    this.isDialogLoading = true;
    await this.fetchSharedDocuments().catch(handleError);

    this.isDialogLoading = false;
    this.step = StepWindow.PICK;
    this.selectedSharing = null;
    this.isDialogActive = true;
  }

  get sharedSharings(): ISharedContent[] {
    if (!this.selectedDocument) return [];
    return this.sharedContents.filter(sc =>
      (sc.refs || []).some(r => r.refId === this.selectedDocument?.id && r.refType === BackendResourceEnum.DOCUMENT)
    );
  }

  get unsharedSharings(): ISharedContent[] {
    if (!this.selectedDocument) return this.sharedContents;
    return this.sharedContents.filter(
      sc =>
        !(sc.refs || []).some(r => r.refId === this.selectedDocument?.id && r.refType === BackendResourceEnum.DOCUMENT)
    );
  }

  selectSharing(sharedContent: ISharedContent) {
    this.selectedSharing = sharedContent;
    this.step = StepWindow.CONFIRM;
  }

  get selectedSharingHasDocument(): boolean {
    if (!this.selectedDocument || !this.selectedSharing) return false;
    return (this.selectedSharing.refs || []).some(
      r => r.refId === this.selectedDocument?.id && r.refType === BackendResourceEnum.DOCUMENT
    );
  }

  get canConfirm(): boolean {
    return this.step === StepWindow.CONFIRM && !!this.selectedSharing && !!this.selectedDocument;
  }

  get dialogSubtitle(): string {
    if (this.step === StepWindow.PICK) {
      return this.$t("report.fleet.documentSharingDialog.subtitle").toString();
    }

    if (this.step === StepWindow.CONFIRM) {
      if (!this.selectedSharingHasDocument) {
        return this.$t("report.fleet.documentSharingDialog.documentNotSharedConfirmAdd").toString();
      }
      return this.$t("report.fleet.documentSharingDialog.documentAlreadySharedConfirmRemove").toString();
    }
    return "";
  }

  get dialogLeftText(): string {
    return this.step === StepWindow.CONFIRM ? this.$t("back").toString() : this.$t("close").toString();
  }

  get dialogRightText(): string {
    if (this.step !== StepWindow.CONFIRM) return this.$t("continue").toString?.() || this.$t("ok").toString();
    return this.selectedSharingHasDocument ? this.$t("remove").toString() : this.$t("add").toString();
  }

  async onLeftClick() {
    if (this.step === StepWindow.CONFIRM) {
      this.step = StepWindow.PICK;
      return;
    }
    this.closeShareDialog();
  }

  async onRightClick() {
    if (this.step !== StepWindow.CONFIRM) return;
    await this.update();
  }

  async update() {
    if (!this.selectedDocument || !this.selectedSharing) return;

    this.isDialogLoading = true;

    const docRef: IReference = {
      refId: this.selectedDocument.id,
      refType: BackendResourceEnum.DOCUMENT
    } as IReference;

    try {
      if (this.selectedSharingHasDocument) {
        this.selectedSharing.refs = (this.selectedSharing.refs || []).filter(
          r => !(r.refId === docRef.refId && r.refType === docRef.refType)
        );
        await this.selectedSharing.update();
      } else {
        await this.selectedSharing.addRef(docRef);
      }

      this.closeShareDialog();
    } catch (e) {
      handleError(e);
    } finally {
      this.isDialogLoading = false;
    }
  }

  closeShareDialog() {
    this.isDialogActive = false;
    this.selectedDocument = null;
    this.selectedSharing = null;
    this.step = StepWindow.PICK;
  }
}

export default PartnerReportVehicleFleetDocumentTable;
